import React, {
    useState, useEffect, useMemo, useCallback, useRef
} from 'react';
import { CONSTRUCTOR_PODS } from 'constants/constructorConstants';
import anaUtils from 'analytics/utils';
import { useGraphSimilarProductRecommendations } from 'hooks/useGraphRecommendations';

const NUM_RESULTS = 6;

export const useFilteredRecommendations = (currentProduct, podIdOverride) => {
    const [podId, setPodId] = useState(podIdOverride || null);

    const { recommendations, updateRecommendations, isFetching } = useGraphSimilarProductRecommendations();
    const fetchInitiatedRef = useRef(false);

    const constructorPayload = useMemo(() => {
        if (!podId) {
            return null;
        }

        const payload = {
            request: {
                numResults: NUM_RESULTS,
                podId,
                itemIds: [currentProduct?.productDetails?.productId]
            },
            additionalParams: {
                quantity: 3,
                sentimentTypes: ['PROS'],
                sentimentSources: ['BV']
            }
        };

        if (podId === CONSTRUCTOR_PODS.SIMILAR_PRODUCTS_UNDER_PRICE) {
            const priceAsNumber = anaUtils.removeCurrencySymbol(currentProduct?.currentSku?.listPrice);

            if (priceAsNumber) {
                payload.request = {
                    ...payload.request,
                    preFilterExpression: {
                        name: 'Price',
                        range: { min: '0', max: priceAsNumber }
                    }
                };
            }
        }

        return payload;
    }, [podId, currentProduct?.productDetails?.productId]);

    useEffect(() => {
        if (podIdOverride) {
            return;
        }

        const similarProductVariants = currentProduct?.cxsContent?.similarProductsComparison?.featuresData;

        if (similarProductVariants?.length) {
            setPodId(similarProductVariants[0].key);
        }
    }, [currentProduct, podIdOverride]);

    // Fetch recommendations when filter changes
    useEffect(() => {
        if (!constructorPayload) {
            return;
        }

        fetchInitiatedRef.current = true;
        updateRecommendations(constructorPayload);
    }, [constructorPayload]);

    const updateFilter = useCallback(
        key => {
            if (!podIdOverride) {
                setPodId(key);
            }
        },
        [podIdOverride]
    );

    const effectiveIsFetching = podIdOverride ? isFetching || !fetchInitiatedRef.current : isFetching;

    return {
        podId,
        isFetching: effectiveIsFetching,
        productRecommendationsData: recommendations[podId] || {},
        updateFilter
    };
};
